/**
 * ChurchJoinPage — public "Join Our Church" application form.
 *
 * Route: /church/:churchId/join   (public — no auth)
 *
 * Fields collected:
 *   - Full name (required — the only mandatory field besides consent)
 *   - Email / Phone (both optional; Phone uses PhoneInput with country picker)
 *   - Date of birth
 *   - Home address (optional)
 *   - "How did you hear about us?" dropdown
 *   - Optional extras: marital status, spouse name, family notes, baptism,
 *     prayer request
 *   - GDPR-style privacy notice + consent checkbox (required)
 *
 * All PII is encrypted at rest server-side. Region-appropriate privacy
 * notice is fetched from /api/public/churches/{id}/privacy-notice on load.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Church, User, Mail, Phone as PhoneIcon, Calendar, MapPin, HeartHandshake,
  Users, ShieldCheck, Loader2, CheckCircle2, Sparkles, ArrowLeft,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import PhoneInput from '@/components/PhoneInput';
import { captureSource, getSource } from '@/lib/qrSource';

const API = process.env.REACT_APP_BACKEND_URL;

const HEARD_ABOUT_OPTIONS = [
  { value: 'friend_family', label: 'A friend or family member' },
  { value: 'social_media', label: 'Social media' },
  { value: 'google_search', label: 'Google search' },
  { value: 'event_outreach', label: 'An event or outreach' },
  { value: 'radio_tv', label: 'Radio or TV' },
  { value: 'walked_in', label: 'I walked in' },
  { value: 'other', label: 'Other' },
];

const MARITAL_OPTIONS = [
  { value: '', label: '— prefer not to say —' },
  { value: 'single', label: 'Single' },
  { value: 'engaged', label: 'Engaged' },
  { value: 'married', label: 'Married' },
  { value: 'separated', label: 'Separated' },
  { value: 'divorced', label: 'Divorced' },
  { value: 'widowed', label: 'Widowed' },
];

export default function ChurchJoinPage() {
  const { churchId } = useParams();
  const [church, setChurch] = useState(null);
  const [privacy, setPrivacy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone_e164: '',
    phone_country: 'US',
    phone_dial: '+1',
    date_of_birth: '',
    address: '',
    heard_about: '',
    marital_status: '',
    spouse_name: '',
    family_notes: '',
    baptism: {
      baptized: false,
      baptism_date: '',
      baptism_place: '',
      baptized_by: '',
    },
    prayer_request: '',
    children: [],
    guardian_consent: false,
    consent: false,
  });

  useEffect(() => {
    captureSource();
    (async () => {
      try {
        const [c, p] = await Promise.all([
          axios.get(`${API}/api/public/churches/${churchId}/summary`),
          axios.get(`${API}/api/public/churches/${churchId}/privacy-notice`),
        ]);
        setChurch(c.data);
        setPrivacy(p.data);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Church not found');
      } finally {
        setLoading(false);
      }
    })();
  }, [churchId]);

  // Name + consent are the only hard requirements. Contact details and
  // address can be filled in later by the leader who follows the person up —
  // children in particular have no contact of their own.
  const canSubmit = useMemo(() => {
    if (!form.full_name.trim() || form.full_name.trim().length < 2) return false;
    if (!form.consent) return false;
    // A parent may consent for their own child, and only by saying so.
    const named = form.children.filter((c) => c.full_name.trim().length > 1);
    if (named.length && !form.guardian_consent) return false;
    return true;
  }, [form]);

  const submit = async () => {
    if (!canSubmit) {
      toast.error('Please give us a name and accept the privacy notice.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone_e164.trim(),
        phone_country: form.phone_dial || '',
        date_of_birth: form.date_of_birth || '',
        address: form.address.trim(),
        heard_about: form.heard_about,
        marital_status: form.marital_status || '',
        spouse_name: form.spouse_name.trim(),
        family_notes: form.family_notes.trim(),
        baptism: form.baptism.baptized ? form.baptism : null,
        prayer_request: form.prayer_request.trim(),
        children: form.children
          .filter((c) => c.full_name.trim().length > 1)
          .map((c) => ({ full_name: c.full_name.trim(),
                         date_of_birth: c.date_of_birth || '',
                         relationship: 'child' })),
        guardian_consent: form.guardian_consent,
        source_code: getSource(),
        consent: {
          accepted: true,
          version: privacy?.version || '1.0',
        },
      };
      await axios.post(
        `${API}/api/public/churches/${churchId}/membership-application`,
        payload,
      );
      setSubmitted(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unable to submit your application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-400 flex items-center justify-center" data-testid="join-loading">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }
  if (error || !church) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-center gap-3 p-6" data-testid="join-error">
        <p className="text-lg font-semibold text-rose-300">Church not found</p>
        <p className="text-sm text-slate-400 max-w-md text-center">{error || 'Please check the link and try again.'}</p>
        <Link to="/" className="text-xs text-violet-300 hover:underline">
          <ArrowLeft className="mr-1 inline-block h-3 w-3" /> Go home
        </Link>
      </div>
    );
  }

  if (submitted) {
    return <SubmittedView church={church} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-indigo-950/80 to-slate-950 text-slate-100" data-testid="join-page">
      {/* Hero */}
      <div className="border-b border-white/5 bg-slate-950/60 backdrop-blur">
        <div className="mx-auto max-w-2xl px-4 py-8 text-center">
          {church.logo_url ? (
            <img src={church.logo_url} alt="" className="mx-auto h-14 mb-3 rounded" />
          ) : (
            <Church className="mx-auto h-10 w-10 text-violet-300 mb-3" />
          )}
          <div className="text-[10px] uppercase tracking-widest text-violet-300">Membership application</div>
          <h1 className="text-2xl sm:text-3xl font-bold mt-1" data-testid="join-church-name">Join {church.name}</h1>
          <p className="text-sm text-slate-400 mt-2 max-w-md mx-auto">
            We&apos;re so glad you&apos;re here. Fill this out and someone from
            our team will reach out to welcome you.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-2xl px-4 py-6 pb-48 space-y-5">
        {/* Basic info */}
        <SectionCard icon={User} title="About you">
          <Row label="Full name" required>
            <Input
              data-testid="join-full-name"
              value={form.full_name}
              onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
              placeholder="Your name as you'd like it in our records"
              className="bg-slate-900 border-slate-700 text-slate-100"
            />
          </Row>
          <Row label="Date of birth">
            <Input
              type="date"
              data-testid="join-dob"
              value={form.date_of_birth}
              onChange={(e) => setForm((f) => ({ ...f, date_of_birth: e.target.value }))}
              className="bg-slate-900 border-slate-700 text-slate-100"
            />
          </Row>
        </SectionCard>

        {/* Contact */}
        <SectionCard icon={Mail} title="How can we reach you?"
          subtitle="All optional — leave anything blank and we can add it later.">
          <Row label="Email (optional)">
            <Input
              type="email"
              data-testid="join-email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="you@example.com"
              className="bg-slate-900 border-slate-700 text-slate-100"
            />
          </Row>
          <Row label="Phone / WhatsApp (optional)">
            <PhoneInput
              testId="join-phone"
              value={form.phone_e164}
              country={form.phone_country}
              onChange={({ e164, country, dial }) => setForm((f) => ({
                ...f,
                phone_e164: e164 || '',
                phone_country: country || f.phone_country,
                phone_dial: dial || f.phone_dial,
              }))}
            />
          </Row>
          <Row label="Home address (optional)">
            <Textarea
              data-testid="join-address"
              value={form.address}
              onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
              placeholder="Street, city, country"
              rows={2}
              className="bg-slate-900 border-slate-700 text-slate-100"
            />
          </Row>
        </SectionCard>

        {/* Discovery */}
        <SectionCard icon={Sparkles} title="How did you hear about us?">
          <Row label="Please choose one (optional)">
            <select
              data-testid="join-heard-about"
              value={form.heard_about}
              onChange={(e) => setForm((f) => ({ ...f, heard_about: e.target.value }))}
              className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
            >
              <option value="">— pick one —</option>
              {HEARD_ABOUT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Row>
        </SectionCard>

        {/* Family & marital */}
        <SectionCard icon={Users} title="Family (optional)">
          <Row label="Marital status">
            <select
              data-testid="join-marital"
              value={form.marital_status}
              onChange={(e) => setForm((f) => ({ ...f, marital_status: e.target.value }))}
              className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
            >
              {MARITAL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Row>
          {(form.marital_status === 'married' || form.marital_status === 'engaged') && (
            <Row label="Spouse's name">
              <Input
                data-testid="join-spouse-name"
                value={form.spouse_name}
                onChange={(e) => setForm((f) => ({ ...f, spouse_name: e.target.value }))}
                placeholder="Their full name"
                className="bg-slate-900 border-slate-700 text-slate-100"
              />
            </Row>
          )}
          <Row label="Family notes" subtitle="Anything else about your household that would help us.">
            <Textarea
              data-testid="join-family-notes"
              value={form.family_notes}
              onChange={(e) => setForm((f) => ({ ...f, family_notes: e.target.value }))}
              rows={2}
              className="bg-slate-900 border-slate-700 text-slate-100"
            />
          </Row>

          <Row label="Your children"
               subtitle="Add the children you are registering with you. Their details stay with this church and are never shown publicly.">
            <div className="space-y-2">
              {form.children.map((child, i) => (
                <div key={i} className="space-y-2 rounded-lg border border-slate-800 p-2 sm:flex sm:items-center sm:gap-2 sm:space-y-0 sm:border-0 sm:p-0">
                  <Input
                    data-testid={`join-child-name-${i}`}
                    value={child.full_name}
                    onChange={(e) => setForm((f) => {
                      const children = [...f.children];
                      children[i] = { ...child, full_name: e.target.value };
                      return { ...f, children };
                    })}
                    placeholder="Child's full name"
                    className="w-full min-w-0 bg-slate-900 border-slate-700 text-slate-100 sm:flex-1"
                  />
                  <div className="flex items-center gap-2">
                    <Input
                      data-testid={`join-child-dob-${i}`}
                      type="date"
                      value={child.date_of_birth}
                      onChange={(e) => setForm((f) => {
                        const children = [...f.children];
                        children[i] = { ...child, date_of_birth: e.target.value };
                        return { ...f, children };
                      })}
                      className="min-w-0 flex-1 bg-slate-900 border-slate-700 text-slate-100 sm:w-44 sm:flex-none"
                    />
                    <Button
                      type="button" variant="ghost"
                      className="min-h-[44px] shrink-0 text-rose-400"
                      data-testid={`join-child-remove-${i}`}
                      onClick={() => setForm((f) => ({
                        ...f, children: f.children.filter((_, j) => j !== i),
                      }))}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
              <Button
                type="button" variant="outline"
                className="min-h-[44px] border-slate-700 text-slate-200"
                data-testid="join-child-add"
                onClick={() => setForm((f) => ({
                  ...f, children: [...f.children, { full_name: '', date_of_birth: '' }],
                }))}
              >
                Add a child
              </Button>
              {form.children.length ? (
                <label className="flex items-start gap-2 pt-1 text-sm text-slate-300"
                       htmlFor="join-guardian-consent">
                  <Checkbox
                    id="join-guardian-consent"
                    data-testid="join-guardian-consent"
                    checked={form.guardian_consent}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, guardian_consent: Boolean(v) }))}
                    className="mt-0.5"
                  />
                  <span>
                    I am the parent or legal guardian of the children named above and
                    I consent on their behalf. The church will contact me, not them.
                  </span>
                </label>
              ) : null}
            </div>
          </Row>
        </SectionCard>

        {/* Baptism */}
        <SectionCard icon={HeartHandshake} title="Baptism (optional)">
          <div className="flex items-start gap-2">
            <Checkbox
              id="baptized"
              data-testid="join-baptized"
              checked={form.baptism.baptized}
              onCheckedChange={(v) => setForm((f) => ({
                ...f,
                baptism: { ...f.baptism, baptized: Boolean(v) },
              }))}
            />
            <Label htmlFor="baptized" className="text-sm text-slate-200">
              I have been baptised
            </Label>
          </div>
          {form.baptism.baptized && (
            <div className="mt-3 space-y-3">
              <Row label="When were you baptised?">
                <Input
                  type="date"
                  data-testid="join-baptism-date"
                  value={form.baptism.baptism_date}
                  onChange={(e) => setForm((f) => ({
                    ...f, baptism: { ...f.baptism, baptism_date: e.target.value },
                  }))}
                  className="bg-slate-900 border-slate-700 text-slate-100"
                />
              </Row>
              <Row label="Where were you baptised?">
                <Input
                  data-testid="join-baptism-place"
                  value={form.baptism.baptism_place}
                  onChange={(e) => setForm((f) => ({
                    ...f, baptism: { ...f.baptism, baptism_place: e.target.value },
                  }))}
                  placeholder="Church name / location"
                  className="bg-slate-900 border-slate-700 text-slate-100"
                />
              </Row>
              <Row label="Baptised by">
                <Input
                  data-testid="join-baptized-by"
                  value={form.baptism.baptized_by}
                  onChange={(e) => setForm((f) => ({
                    ...f, baptism: { ...f.baptism, baptized_by: e.target.value },
                  }))}
                  placeholder="Minister / pastor"
                  className="bg-slate-900 border-slate-700 text-slate-100"
                />
              </Row>
            </div>
          )}
        </SectionCard>

        {/* Prayer request */}
        <SectionCard icon={HeartHandshake} title="Anything we can pray for? (optional)">
          <Textarea
            data-testid="join-prayer-request"
            value={form.prayer_request}
            onChange={(e) => setForm((f) => ({ ...f, prayer_request: e.target.value }))}
            rows={3}
            placeholder="Your prayer request will be seen only by the pastoral team."
            className="bg-slate-900 border-slate-700 text-slate-100"
          />
        </SectionCard>

        {/* Privacy + consent */}
        <PrivacyBlock
          privacy={privacy}
          consented={form.consent}
          onConsent={(v) => setForm((f) => ({ ...f, consent: Boolean(v) }))}
        />

        {/* Submit */}
        <div className="pt-2">
          <Button
            data-testid="join-submit-btn"
            onClick={submit}
            disabled={!canSubmit || submitting}
            className="w-full bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-400 hover:to-fuchsia-400 text-white"
            size="lg"
          >
            {submitting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
            ) : (
              <>Submit application</>
            )}
          </Button>
          <p className="text-[11px] text-center text-slate-500 mt-2">
            Your information is encrypted at rest and only visible to the {church.name} team.
          </p>
        </div>
      </div>
    </div>
  );
}


function SubmittedView({ church }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-emerald-950/80 to-slate-950 text-slate-100 flex flex-col items-center justify-center p-6 text-center" data-testid="join-submitted">
      <CheckCircle2 className="h-14 w-14 text-emerald-300 mb-4" />
      <h1 className="text-2xl sm:text-3xl font-bold">Thank you!</h1>
      <p className="text-sm text-slate-300 max-w-md mt-2">
        Your application to join <span className="font-semibold text-white">{church.name}</span> has been received.
      </p>
      <p className="text-sm text-slate-400 max-w-md mt-2">
        A team member will reach out to you soon. If you have any questions in the
        meantime, feel free to contact the church directly.
      </p>
      <Link to="/" className="mt-6 text-xs text-emerald-300 hover:underline">
        <ArrowLeft className="mr-1 inline-block h-3 w-3" /> Back home
      </Link>
    </div>
  );
}


function SectionCard({ icon: Icon, title, subtitle, children }) {
  return (
    <section className="rounded-xl border border-white/10 bg-slate-900/40 p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4 text-violet-300" />
        <h2 className="text-sm font-semibold text-slate-100">{title}</h2>
      </div>
      {subtitle && <p className="text-[11px] text-slate-400 -mt-2 mb-3">{subtitle}</p>}
      <div className="space-y-3">{children}</div>
    </section>
  );
}


function Row({ label, subtitle, required, children }) {
  return (
    <div>
      <Label className="text-xs text-slate-300">
        {label}{required && <span className="text-rose-400 ml-1">*</span>}
      </Label>
      {subtitle && <p className="text-[11px] text-slate-500 mb-1">{subtitle}</p>}
      <div className="mt-1">{children}</div>
    </div>
  );
}


function PrivacyBlock({ privacy, consented, onConsent }) {
  const [expanded, setExpanded] = useState(false);
  if (!privacy) return null;
  return (
    <section
      data-testid="join-privacy-block"
      className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 sm:p-5"
    >
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="h-4 w-4 text-amber-300" />
        <h2 className="text-sm font-semibold text-slate-100">Your privacy</h2>
      </div>
      <p className="text-[12px] text-slate-300 leading-relaxed">{privacy.purpose}</p>
      <button
        type="button"
        data-testid="join-privacy-toggle"
        onClick={() => setExpanded((x) => !x)}
        className="mt-2 text-[11px] text-amber-300 hover:underline"
      >
        {expanded ? 'Hide details' : 'Read the full privacy notice'}
      </button>
      {expanded && (
        <div className="mt-3 space-y-2 text-[11px] text-slate-300 border-t border-amber-500/20 pt-3">
          <p><b className="text-slate-100">Who controls this data:</b> {privacy.controller}</p>
          <p><b className="text-slate-100">Legal basis:</b> {privacy.legal_basis}</p>
          <p><b className="text-slate-100">Retention:</b> {privacy.retention}</p>
          <p><b className="text-slate-100">Sensitive data:</b> {privacy.sensitive_data_notice}</p>
          <div>
            <b className="text-slate-100">Your rights:</b>
            <ul className="list-disc pl-5 mt-0.5">
              {(privacy.rights || []).map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </div>
          <p><b className="text-slate-100">Complain to:</b> {privacy.authority}</p>
          <p className="opacity-70">Version {privacy.version} · Region: {privacy.region}</p>
        </div>
      )}
      <div className="mt-4 flex items-start gap-2">
        <Checkbox
          id="join-consent"
          data-testid="join-consent-checkbox"
          checked={consented}
          onCheckedChange={onConsent}
        />
        <Label htmlFor="join-consent" className="text-xs text-slate-200 leading-relaxed">
          I&apos;ve read the privacy notice and consent to <span className="font-semibold">{privacy.controller}</span> processing my
          information for the purposes above. <span className="text-rose-400">*</span>
        </Label>
      </div>
    </section>
  );
}
